import { Brand } from '../../services/brand';

// Extend the Brand interface to match what's expected in this component
export interface ExtendedBrand extends Brand {
  groupId: string; // Add missing properties used in the component
  token: string;   // Add token property for QR code generation
  logs?: string[]; // Add logs property
}

// Local interface for form handling (different from API interface)
export interface BrandFormData {
  id: string;
  name: string;
  groupId: string;
  contactNo?: string;
  contactEmail?: string;
  isEv: boolean;
  isBrand?: boolean;
  isPublic: boolean;
  category: Array<string>;
  subCategory: Array<string>;
  schemeBucket: Array<string>;
  dashboard: boolean;
  suspended: boolean;
  publicFor: Array<string>;
  lender: string;
  kycGstNo: string;
  kycBank?: {
    accountNo?: string;
    holderName?: string;
    ifsc?: string;
  };
  kycShop?: {
    name?: string;
    address?: string;
    state?: string;
    city?: string;
    pin?: string;
  };
  kycDocs?: {
    shopFront: string | null;
    shopInner: string | null;
    evCer: string | null;
  };
  kyc?: {
    status?: string;
    bank?: boolean;
    shop?: boolean;
    docs?: boolean;
  };
}

export interface FetchResult {
  Brands: Brand[];
  totalMatch: number;
  pageNo: number;
  pageSize: number;
  clientPaginated?: boolean;
  paginationMessage?: string;
}

// Helper functions to safely access nested KYC properties
export const getBankAccountNo = (Brand: ExtendedBrand): string | null => {
  if (Brand.kycBank && Brand.kycBank.accountNo) {
    return Brand.kycBank.accountNo;
  }
  return null;
};

export const getBankHolderName = (Brand: ExtendedBrand): string | null => {
  if (Brand.kycBank && Brand.kycBank.holderName) {
    return Brand.kycBank.holderName;
  }
  return null;
};

export const getBankIfsc = (Brand: ExtendedBrand): string | null => {
  if (Brand.kycBank && Brand.kycBank.ifsc) {
    return Brand.kycBank.ifsc;
  }
  return null;
};

export const getShopName = (Brand: ExtendedBrand): string | null => {
  if (Brand.kycShop && Brand.kycShop.name) {
    return Brand.kycShop.name;
  }
  return null;
};

export const getShopAddress = (Brand: ExtendedBrand): string | null => {
  if (Brand.kycShop && Brand.kycShop.address) {
    return Brand.kycShop.address;
  }
  return null;
};

export const getShopCity = (Brand: ExtendedBrand): string | null => {
  if (Brand.kycShop && Brand.kycShop.city) {
    return Brand.kycShop.city;
  }
  return null;
};

export const getShopState = (Brand: ExtendedBrand): string | null => {
  if (Brand.kycShop && Brand.kycShop.state) {
    return Brand.kycShop.state;
  }
  return null;
};

export const getShopPin = (Brand: ExtendedBrand): string | null => {
  if (Brand.kycShop && Brand.kycShop.pin) {
    return Brand.kycShop.pin;
  }
  return null;
};

export const getDocShopFront = (Brand: ExtendedBrand): string | null => {
  if (Brand.kycDocs && Brand.kycDocs.shopFront) {
    return Brand.kycDocs.shopFront;
  }
  return null;
};

export const getDocShopInner = (Brand: ExtendedBrand): string | null => {
  if (Brand.kycDocs && Brand.kycDocs.shopInner) {
    return Brand.kycDocs.shopInner;
  }
  return null;
};

export const getDocEvCertificate = (Brand: ExtendedBrand): string | null => {
  if (Brand.kycDocs && Brand.kycDocs.evCer) {
    return Brand.kycDocs.evCer;
  }
  return null;
};

// Format date
export const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Helper function to get lender badge color based on lender name
export const getLenderBadgeColor = (lender: string): string => {
  // Convert lender to lowercase for case-insensitive matching
  const lenderLower = lender.toLowerCase().trim();

  // Current specific lenders
  if (lenderLower === 'cashe' || lenderLower.includes('cashe')) {
    return 'bg-yellow-500 text-white';
  } else if (lenderLower === 'zype' || lenderLower.includes('zype')) {
    return 'bg-black text-white';
  } else if (lenderLower === 'general' || lenderLower.includes('general')) {
    return 'bg-blue-500 text-white';
  }
  return 'bg-gray-200 text-gray-900';
};

// Form validation function
export const validateForm = (data: BrandFormData, setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>) => {
  const newErrors: Record<string, string> = {};
  let isValid = true;

  // Validate name
  if (!data.name || data.name.trim() === '') {
    newErrors.name = 'Name is required';
    isValid = false;
  }

  // Validate contact number (optional)
  if (data.contactNo && data.contactNo.trim() !== '') {
    if (!/^[0-9]{10}$/.test(data.contactNo)) {
      newErrors.contactNo = 'Contact number must be 10 digits';
      isValid = false;
    }
  }

  // Validate email (optional)
  if (data.contactEmail && data.contactEmail.trim() !== '') {
    if (!/\S+@\S+\.\S+/.test(data.contactEmail)) {
      newErrors.contactEmail = 'Invalid email format';
      isValid = false;
    }
  }


  setErrors(newErrors);
  return isValid;
}; 